
import Vue from 'vue';
import * as names from "@/store/names";
import router from '@/router/index'
import axios from '@/utils/api'
import i18n from '@/locales/index'
import { isGenericKlearningHost, buildSubdomainUrl } from '@/utils/utils'

// Evita saltos duplicados al subdominio: en el callback SSO conviven dos flujos
// que encadenan fetchUserData (el restoreSession de main.js por kl_session_hint,
// y azureCallback desde Login.vue), así que fetchUserInstitution corre dos veces
// casi a la vez. window.location.href no detiene el JS en curso, por lo que el
// segundo salto —ya sin el redirect, consumido por el primero— pisaría al bueno
// y dejaría al usuario en el home. Esta guarda asegura un único salto.
let subdomainRedirectInFlight = false;

export const login = ({ commit, dispatch }, user_data) => {
    // SEC-3 fase 2: withCredentials para que el navegador guarde la cookie
    // HttpOnly con el refresh token. El body puede traer o no 'refresh'
    // (flag de compat JWT_REFRESH_IN_BODY en el backend) — se ignora: solo
    // se conserva el access token, en memoria.
    axios.post('/token/', { ...user_data, frontend_origin: window.location.origin },
        { withCredentials: true })
        .then((response) => {
            const token = response.data.access;
            commit('setTokens', { token });
            commit('setIsLoged', true);
            // Persistir el destino ANTES de dispatch/push: cuando fetchUserInstitution
            // corra y decida saltar al subdominio, router.currentRoute ya habrá cambiado
            // por el push de abajo y la query 'redirect' se habrá perdido. Mismo canal
            // (localStorage) que usa el SSO en initiateSSOLogin. Se limpia si no hay
            // destino, para no arrastrar uno viejo a un login posterior.
            const queryRedirect = router.currentRoute.query.redirect;
            if (typeof queryRedirect === 'string' && queryRedirect) {
                localStorage.setItem('sso_post_login_redirect', queryRedirect);
            } else {
                localStorage.removeItem('sso_post_login_redirect');
            }
            dispatch('fetchUserData');
            // Redirigir al usuario a la ruta original o al dashboard
            router.push(queryRedirect || { name: 'DashboardView' });
        })
        .catch((error) => {
            const detail = error.response && error.response.data && error.response.data.detail;
            Vue.swal.fire({
                title: "Login Error",
                text: detail || "Compruebe su nombre de usuario y clave.",
                type: "warning",
            });
        })
};

// SEC-3 fase 2: restaura la sesión usando la cookie HttpOnly (silent refresh).
// Se usa al recargar la página (bootstrap de main.js) y tras el redirect SSO
// (el backend ya dejó la cookie; los tokens ya no viajan por la URL).
// Encadena fetchUserData para que v-can/guard vean permisos al primer render.
export const restoreSession = ({ commit, dispatch }) => {
    return axios.post('/token/refresh/', {}, { withCredentials: true })
        .then((response) => {
            const token = response.data.access;
            commit('setAccessToken', token);
            commit('setIsLoged', true);
            return dispatch('fetchUserData');
        })
        .catch((error) => {
            // Sin cookie / cookie vencida o blacklisteada: no hay sesión.
            // El hint solo se limpia ante un rechazo REAL de auth (401/485):
            // un error de red transitorio no debe dejar a todas las pestañas
            // sin restauración de sesión (el hint es compartido via
            // localStorage y esto pega justo en la ola de recargas post-deploy).
            const status = error.response && error.response.status;
            if (status === 401 || status === 485) {
                localStorage.removeItem('kl_session_hint');
            }
            return Promise.reject(error);
        });
};

export const azureCallback = ({ dispatch }) => {
    // Azure SSO callback handler.
    // SEC-3 fase 2: el backend ya no manda tokens por la URL — dejó la cookie
    // HttpOnly en el redirect; la sesión se establece vía silent refresh.
    return dispatch('restoreSession').then(() => {
        // El roundtrip OAuth borró la query original: el destino post-login lo
        // dejó initiateSSOLogin en localStorage. Se lee acá (fetchUserInstitution
        // lo consume/borra al cerrar el flujo). En el caso subdominio este push
        // queda superado por el salto cross-origin de fetchUserInstitution.
        const queryRedirect = typeof router.currentRoute.query.redirect === 'string'
            ? router.currentRoute.query.redirect
            : null;
        const savedRedirect = localStorage.getItem('sso_post_login_redirect');
        const redirectPath = queryRedirect || savedRedirect || { name: 'DashboardView' };
        router.push(redirectPath).catch(() => { });
    });
};

// Sesión propia: una sola petición a /authentication/me/ con los datos del usuario.
// Antes eran dos (detalle por id + permissions) y el detalle llegaba con dirección,
// teléfonos y datos de SSO que ninguna vista lee.
// Los permisos van aparte (with_permissions) porque pesan el 97% de la respuesta y
// solo los necesita el arranque de sesión; los refrescos del usuario no los piden.
// Resuelve con el usuario ya comiteado, para quien necesite leerlo de vuelta.
export const fetchMe = ({ commit }, { with_permissions = false } = {}) => {
    const url = with_permissions ? '/authentication/me/?permissions=True' : '/authentication/me/';
    return axios.get(url)
        .then((response) => {
            const { permissions, ...user } = response.data;
            commit('setUser', user);
            // Sin la clave no se toca el store: un refresco no debe borrar los permisos.
            if (permissions) commit('setUserPermissions', permissions);
            return user;
        });
};

// SEC-3: retorna una promesa que resuelve cuando user + permissions están en el
// store (main.js la espera antes de montar la app al recargar, para que v-can y
// el guard del router vean los permisos en el primer render). Institution y
// position siguen siendo fire-and-forget como siempre.
export const fetchUserData = ({ dispatch }) => {
    return dispatch('fetchMe', { with_permissions: true })
        .then((user) => {
            dispatch('fetchUserInstitution', user.school);
            dispatch(names.FETCH_CURRENT_POSITION, user.id);
        })
        .catch((error) => {
            console.log(error);
        });
};

export const fetchUserInstitution = ({ commit, dispatch, state }, institution_id) => {
    axios.get(`/mesh/institution/${institution_id}/`)
        .then((response) => {
            commit('setUserInstitution', response.data);

            // Único punto por el que pasan todos los flujos de login (password,
            // restoreSession al recargar, y SSO vía azureCallback -> restoreSession)
            // después de resolver la institución del usuario: si estamos en el
            // dominio genérico klearning.cl y la institución tiene subdominio
            // propio desplegado, saltar ahí. kl_session_redirect le indica a
            // main.js que intente restoreSession aunque el hint de localStorage
            // (que no cruza de origin) todavía no exista en el destino.
            if (isGenericKlearningHost(window.location.hostname) && !subdomainRedirectInFlight) {
                // El destino post-login viene en la query (?redirect=) o, si el
                // roundtrip OAuth de SSO borró la query, en localStorage (lo dejó
                // initiateSSOLogin). El primer flujo que llegue acá lo consume.
                const queryRedirect = typeof router.currentRoute.query.redirect === 'string'
                    ? router.currentRoute.query.redirect
                    : null;
                const savedRedirect = localStorage.getItem('sso_post_login_redirect');
                const postLoginRedirect = queryRedirect || savedRedirect || null;

                const target = buildSubdomainUrl(response.data.subdomain, {
                    redirect: postLoginRedirect,
                    sessionHint: true,
                });
                if (target) {
                    subdomainRedirectInFlight = true;
                    localStorage.removeItem('sso_post_login_redirect');
                    window.location.href = target;
                    return;
                }
            }

            dispatch(names.FETCH_TAXONOMIES);
            dispatch(names.FETCH_CYCLES);
            if (response.data.image) {
                dispatch(names.FETCH_IMAGE, response.data.image).then(image => {
                    commit('setInstitutionLogo', image.image);
                });
            }
            // Resolver idioma del usuario al login
            dispatch('resolveUserLanguage', {
                user: state.user,
                institution: response.data,
            });
        })
        .catch((error) => {
            console.log(error)
        });

};

export const resolveUserLanguage = ({ commit, dispatch }, { user, institution }) => {
    // Usar el store action para evitar peticiones duplicadas si Language.vue ya la disparó
    dispatch(names.FETCH_LANGUAGES, true)
        .then((languages) => {
            const activeLanguages = languages.filter((lang) => lang.is_active);
            const userLangId = user && user.user_data ? user.user_data.default_language : null;
            const institutionLangId = institution ? institution.default_language : null;

            let resolvedCode = 'es'; // fallback

            if (userLangId) {
                // Verificar si el idioma del usuario está activo en la institución
                const userLang = activeLanguages.find((lang) => lang.id === userLangId);
                if (userLang) {
                    // Caso 1: idioma del user_data existe y está activo
                    resolvedCode = userLang.code;
                } else {
                    // Caso 2: idioma del user_data no coincide con los activos → usar idioma de la institución
                    const instLang = activeLanguages.find((lang) => lang.id === institutionLangId);
                    if (instLang) resolvedCode = instLang.code;
                }
            } else {
                // Caso 3: user_data.default_language es null → usar idioma de la institución
                const instLang = activeLanguages.find((lang) => lang.id === institutionLangId);
                if (instLang) resolvedCode = instLang.code;
            }

            // Aplicar idioma resuelto
            localStorage.setItem('user_language', resolvedCode);
            i18n.locale = resolvedCode;
            commit(names.SET_USER_LANGUAGE, resolvedCode);
            // Refrescar alternative_names con el nuevo idioma
            localStorage.removeItem('alternative_names');
            dispatch(names.FETCH_ALTERNATIVE_NAMES, true).then((alternative_names) => {
                localStorage.setItem("alternative_names", JSON.stringify(alternative_names));
            });
        })
        .catch((error) => {
            console.log('Error resolving user language:', error);
        });
};

// #TODO: Eliminar esta action. Hay que revisar unos cuantos archivos. Hacerlo con cuidado.
// SEC-5: revoke server-side (blacklist refresh tokens + must_relog) BEFORE clearing
// local state, so the request still carries a valid token. Best-effort — if the call
// fails (offline / already expired) we still clear the local session below.
// SEC-3 fase 2: el refresh va en la cookie HttpOnly (withCredentials), no en el
// body; el backend la blacklistea y la borra (Set-Cookie expirada).
export const logOut = async ({ commit }) => {
    try {
        await axios.post('/logout/', {}, { timeout: 4000, withCredentials: true });
    } catch (e) {
        // ignore — local session is cleared regardless
    }
    commit('clearLoginData');
};

export const updateToken = ({ commit }) => {
    // SEC-3 fase 2: el refresh token viaja en la cookie HttpOnly, no en el body.
    // Retorna la promesa (y propaga el fallo) para que el caller decida qué
    // hacer si la cookie ya no sirve. En fallo solo se limpia la sesión LOCAL —
    // NUNCA llamar /logout/ desde aquí: blacklistea todos los refresh tokens
    // (must_relog) y cerraría la sesión de las demás pestañas/dispositivos.
    return axios.post('/token/refresh/', {}, { withCredentials: true })
        .then((response) => {
            // (fix) antes hacía commit('setToken') — mutación inexistente (no-op).
            commit('setAccessToken', response.data.access);
        })
        .catch((error) => {
            console.log('Unable to refresh token. ', error);
            commit('clearLoginData');
            return Promise.reject(error);
        });
};
